import { inject } from '@angular/core';
import { CanActivateFn, Route, Router, Routes } from '@angular/router';

import { UserStore } from '@/shared/lib/stores/user.store';

const authGuard: CanActivateFn = () => {
  return inject(UserStore).isAuthenticated() || inject(Router).createUrlTree(['/account/login']);
};

/* Pages rendered outside the tabs shell, available without being logged in */
const publicPages: Routes = [
  {
    path: 'account/login',
    loadComponent: () => import('./pages/account/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'account/forgot-credentials',
    loadComponent: () => import('./pages/account/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
  },
];

/* Detail pages are routed below the page that opens them, so they share its Ionic navigation stack (first URL segment) */
const scanDetailsRoute = (parentPath: string): Route => ({
  path: `${parentPath}scan-details`,
  loadComponent: () => import('./pages/scan/scan-details/scan-details.component').then(m => m.ScanDetailsComponent),
});

const connectorCheckResultsRoute = (parentPath: string): Route => ({
  path: `${parentPath}connector-check-results`,
  loadComponent: () => import('./pages/scan/connector-check-result/connector-check-result.component').then(m => m.ConnectorCheckResultComponent),
});

const realtimeRoute = (parentPath: string): Route => ({
  path: `${parentPath}realtime`,
  loadComponent: () => import('./pages/scan/realtime/realtime.component').then(m => m.RealtimeComponent),
});

const monitoringResultsRoute = (parentPath: string): Route => ({
  path: `${parentPath}monitoring-results`,
  loadComponent: () => import('./pages/monitoring/monitoring-result/monitoring-result.component').then(m => m.MonitoringResultComponent),
});

const homePages: Routes = [
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
  },
];

const accountPages: Routes = [
  {
    path: 'account/my-account',
    loadComponent: () => import('./pages/account/my-account/my-account.component').then(m => m.MyAccountComponent),
  },
];

const supportPages: Routes = [
  {
    path: 'support/contact',
    loadComponent: () => import('./pages/support/contact/contact.component').then(m => m.ContactComponent),
  },
  {
    path: 'support/settings',
    loadComponent: () => import('@/app/app/pages/support/settings/settings.component').then(m => m.SettingsComponent),
  },
  {
    path: 'support/how-to',
    loadComponent: () => import('./pages/support/how-to/how-to.component').then(m => m.HowToComponent),
  },
  {
    path: 'support/device-status',
    loadComponent: () => import('@/app/app/pages/support/device-status/device-status.component').then(m => m.DeviceStatusComponent),
  },
];

const scanPages: Routes = [
  {
    path: 'action/:scanType',
    loadComponent: () => import('./pages/scan/action/action.component').then(m => m.ActionComponent),
  },
  connectorCheckResultsRoute(''),
  realtimeRoute(''),
  {
    path: 'port-mappings',
    loadComponent: () => import('./pages/scan/port-mappings/port-mappings.component').then(m => m.PortMappingsComponent),
  },
];

const historyPages: Routes = [
  {
    path: 'history',
    loadComponent: () => import('./pages/scan/history/history.component').then(m => m.HistoryComponent),
  },
  scanDetailsRoute('history/'),
  connectorCheckResultsRoute('history/'),
  realtimeRoute('history/'),
  monitoringResultsRoute('history/'),
  scanDetailsRoute('history/monitoring-results/'),
  {
    path: 'history/sor-analyzer',
    loadComponent: () => import('./pages/analyzer/sor-analyzer/sor-analyzer.component').then(m => m.SorAnalyzerComponent),
  },
];

const monitoringPages: Routes = [
  {
    path: 'monitoring/:scanType',
    loadComponent: () => import('./pages/monitoring/monitoring/monitoring.component').then(m => m.MonitoringComponent),
  },
  scanDetailsRoute('monitoring/:scanType/'),
  monitoringResultsRoute(''),
  scanDetailsRoute('monitoring-results/'),
  {
    path: 'monitoring-dashboard',
    loadComponent: () => import('./pages/monitoring/monitoring-dashboard/monitoring-dashboard.component').then(m => m.MonitoringDashboardComponent),
  },
  scanDetailsRoute('monitoring-dashboard/'),
];

export const routes: Routes = [
  ...publicPages,
  {
    // Outside the shell on purpose: navigating here destroys the tabs shell (see AppNavigationService.resetToHome)
    path: 'switching',
    loadComponent: () => import('./pages/switching/switching.component').then(m => m.SwitchingComponent),
    canActivate: [authGuard],
  },
  {
    // Authenticated shell: the tabs component owns the router outlet for every protected page
    path: '',
    loadComponent: () => import('./pages/tabs/tabs.component').then(m => m.TabsComponent),
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      ...homePages,
      ...accountPages,
      ...supportPages,
      ...scanPages,
      ...historyPages,
      ...monitoringPages,
    ],
  },
];
